import React from 'react'

import { Campaign } from '@/core/api/entities/campaign/campaign'
import { Group } from '@/core/api/entities/group'
import { useTranslation } from '@/core/i18n/useTranslation'
import { AdCampaignCard } from '@/features/zones/components/AdCampaignCard'
import { AddButton } from '@/features/zones/components/AddButton'

interface AdCampaignsCarouselProps {
	campaigns: Campaign[]
	onAdd?: () => void
	onRemove?: (campaignId: number) => void
	onCampaignClick?: (campaignId: number) => void
	groups?: Group[]
	variant?: 'group' | 'zone'
	subHeader?: React.ReactNode
}

export const AdCampaignsSection: React.FC<AdCampaignsCarouselProps> = ({
	campaigns,
	onAdd,
	onRemove,
	onCampaignClick,
	groups,
	variant = 'group',
	subHeader,
}) => {
	const { t } = useTranslation('zones')

	if (campaigns.length === 0) {
		return (
			<div className="space-y-4 mb-12">
				<div className="flex items-center gap-3">
					<h2 className="text-xl font-semibold">
						{t('details.campaigns.title')}
					</h2>
					{onAdd && <AddButton onAdd={onAdd} />}
				</div>
				{subHeader}
				<div className="text-sm text-muted-foreground">
					{t('details.campaigns.noCampaigns')}
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-4 mb-12">
			<div className="flex items-center gap-3">
				<h2 className="text-xl font-semibold">
					{t('details.campaigns.title')}
				</h2>
				{onAdd && <AddButton onAdd={onAdd} />}
			</div>

			{subHeader}

			<div className="flex flex-col gap-4">
				{campaigns.map((campaign) => {
					// Filter groups that contain this specific campaign
					const campaignGroups =
						groups?.filter((group) =>
							group.campaigns?.some((c) => c.id === campaign.id)
						) || []

					return (
						<AdCampaignCard
							key={campaign.id}
							campaign={campaign}
							onRemove={onRemove ? () => onRemove(campaign.id) : undefined}
							onClick={onCampaignClick}
							groups={campaignGroups}
							variant={variant}
						/>
					)
				})}
			</div>
		</div>
	)
}
